import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { MapPin, ArrowRight, Star, Phone, Shield, TrendingUp, Building2, Home, Landmark, TreePine, ChevronRight, ChevronLeft, Search, Sparkles, Award, Users, Handshake, Clock } from 'lucide-react';
import api from '@/utils/api';

/* ---- Scroll Reveal Hook ---- */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};
const Reveal = ({ children, className = '', dir = 'up', delay = 0 }) => {
  const ref = useReveal();
  const cls = dir === 'left' ? 'reveal-left' : dir === 'right' ? 'reveal-right' : dir === 'scale' ? 'reveal-scale' : 'reveal';
  const stagger = delay ? `stagger-${delay}` : '';
  return <div ref={ref} className={`${cls} ${stagger} ${className}`}>{children}</div>;
};

/* ---- Animated Counter ---- */
const Counter = ({ target, suffix = '' }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const num = parseInt(target) || 0;
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = Math.ceil(num / 40);
        const timer = setInterval(() => { start += step; if (start >= num) { setVal(num); clearInterval(timer); } else setVal(start); }, 30);
        obs.unobserve(e.target);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [num]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const HERO_SLIDES = [
  { img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1400&q=80', title: 'Premium Villas' },
  { img: 'https://images.unsplash.com/photo-1769251846390-772c3a74b0da?w=1400&q=80', title: 'Modern Apartments' },
  { img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1400&q=80', title: 'Dream Homes' },
  { img: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1400&q=80', title: 'Investment Plots' },
];

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [seoPages, setSeoPages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/public/properties').then(r => setProperties(r.data?.slice(0, 6) || [])).catch(() => {});
    api.get('/public/testimonials').then(r => setTestimonials(r.data || [])).catch(() => {});
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
    api.get('/public/seo-pages').then(r => setSeoPages(r.data || [])).catch(() => {});
  }, []);

  const nextSlide = useCallback(() => setCurrentSlide(prev => (prev + 1) % HERO_SLIDES.length), []);
  useEffect(() => { const t = setInterval(nextSlide, 5000); return () => clearInterval(t); }, [nextSlide]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (searchType) params.set('type', searchType);
    navigate(`/properties?${params.toString()}`);
  };

  const locations = [...new Set((seoPages || []).map(p => p.location).filter(Boolean))];
  const statsNum = (v) => parseInt((v || '0').replace(/[^0-9]/g, '')) || 0;

  return (
    <WebsiteLayout>
      <Helmet>
        <title>{settings?.meta_title || 'Ganesa Homestate Developers | Premium Plots, Flats, Villas & Properties'}</title>
        <meta name="description" content={settings?.meta_description || 'Ganesa Homestate Developers - Your trusted partner for premium plots, flats, villas and commercial properties.'} />
        {settings?.meta_keywords && <meta name="keywords" content={settings.meta_keywords} />}
        <meta property="og:title" content={settings?.meta_title || 'Ganesa Homestate Developers'} />
        <meta property="og:description" content={settings?.meta_description || 'Premium real estate developers.'} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={settings?.website || 'https://ganesahomestate.com'} />
      </Helmet>

      {/* =============== HERO =============== */}
      <section className="relative bg-[#0A0A0F] overflow-hidden min-h-[90vh] sm:min-h-[85vh] flex flex-col" data-testid="hero-section">
        {/* Bg Slider */}
        <div className="absolute inset-0">
          {HERO_SLIDES.map((slide, idx) => (
            <div key={idx} className={`absolute inset-0 transition-opacity duration-[1500ms] ${idx === currentSlide ? 'opacity-100 scale-105' : 'opacity-0 scale-100'}`}
              style={{ transition: 'opacity 1.5s ease, transform 8s ease' }}>
              <img src={slide.img} alt={slide.title} className="w-full h-full object-cover" loading={idx === 0 ? 'eager' : 'lazy'} />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0F]/95 via-[#0A0A0F]/80 to-[#0A0A0F]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-transparent to-[#0A0A0F]/30" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-[#DAA520]/5 rounded-full blur-3xl animate-float-slow hidden lg:block" />
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#DAA520]/3 rounded-full blur-3xl animate-float hidden lg:block" />

        {/* Dots */}
        <div className="absolute bottom-6 sm:bottom-10 right-4 sm:right-10 z-20 flex gap-2">
          {HERO_SLIDES.map((_, idx) => (
            <button key={idx} onClick={() => setCurrentSlide(idx)}
              className={`h-2 rounded-full transition-all duration-500 ${idx === currentSlide ? 'bg-[#DAA520] w-8' : 'bg-white/30 w-2 hover:bg-white/50'}`} />
          ))}
        </div>

        {/* Arrows */}
        <button onClick={() => setCurrentSlide(p => (p - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/5 backdrop-blur-md text-white/70 hover:bg-[#DAA520] hover:text-[#0A0A0F] transition-all hidden sm:flex items-center justify-center border border-white/10 hover:border-[#DAA520]">
          <ChevronLeft size={20} />
        </button>
        <button onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/5 backdrop-blur-md text-white/70 hover:bg-[#DAA520] hover:text-[#0A0A0F] transition-all hidden sm:flex items-center justify-center border border-white/10 hover:border-[#DAA520]">
          <ChevronRight size={20} />
        </button>

        {/* Content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full py-10 sm:py-0">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#DAA520]/10 rounded-full text-[#DAA520] text-xs sm:text-sm font-semibold mb-5 sm:mb-8 border border-[#DAA520]/20 backdrop-blur-sm animate-pulse-gold">
                <Sparkles size={14} /> Trusted by 5000+ Families
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-white leading-[1.1] mb-4 sm:mb-6">
                {settings?.hero_title ? settings.hero_title : (
                  <>Premium Properties for Your{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DAA520] via-[#FFD700] to-[#DAA520] animate-gradient">Dream Life</span></>
                )}
              </h1>
              <p className="text-sm sm:text-lg text-slate-400 mb-6 sm:mb-10 leading-relaxed max-w-2xl">
                {settings?.hero_subtitle || 'Discover premium plots, flats, villas and commercial spaces across prime locations. Trusted by 1000+ families.'}
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} data-testid="hero-search-form"
                className="bg-white/[0.07] backdrop-blur-2xl rounded-2xl p-2.5 sm:p-3 border border-white/[0.08] shadow-[0_8px_60px_rgba(0,0,0,0.4)]">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" data-testid="hero-search-input" placeholder="Location, property name..."
                      value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-3.5 bg-white rounded-xl text-[#0A0A0F] placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#DAA520] shadow-sm" />
                  </div>
                  <select data-testid="hero-search-type" value={searchType} onChange={e => setSearchType(e.target.value)}
                    className="py-3.5 px-4 bg-white rounded-xl text-[#0A0A0F] text-sm focus:outline-none focus:ring-2 focus:ring-[#DAA520] sm:w-44 shadow-sm">
                    <option value="">All Types</option>
                    <option value="Plot">Plot</option><option value="Flat">Flat</option><option value="Villa">Villa</option>
                    <option value="Commercial">Commercial</option><option value="Farm House">Farm House</option>
                  </select>
                  <button type="submit" data-testid="hero-search-btn"
                    className="px-7 py-3.5 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold text-sm rounded-xl hover:shadow-[0_8px_30px_rgba(201,168,76,0.4)] transition-all active:scale-95 flex items-center justify-center gap-2">
                    <Search size={16} /> Search
                  </button>
                </div>
              </form>

              {/* Quick Filters */}
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-hide">
                {['Plot', 'Flat', 'Villa', 'Commercial', 'Farm House'].map(type => (
                  <button key={type} onClick={() => navigate(`/properties?type=${type}`)}
                    className="shrink-0 px-4 py-2 bg-white/[0.06] backdrop-blur border border-white/[0.08] rounded-full text-white/80 text-xs sm:text-sm font-medium hover:bg-[#DAA520] hover:text-[#0A0A0F] hover:border-[#DAA520] transition-all active:scale-95 hover:shadow-[0_4px_20px_rgba(201,168,76,0.3)]">
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="relative z-10 border-t border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
            <div className="grid grid-cols-4 gap-3 sm:gap-8">
              {[
                { val: settings?.stats?.projects, label: 'Projects', suffix: '+' },
                { val: settings?.stats?.customers, label: 'Customers', suffix: '+' },
                { val: settings?.stats?.years, label: 'Years', suffix: '+' },
                { val: settings?.stats?.cities, label: 'Cities', suffix: '+' },
              ].map((s, i) => (
                <div key={i} className="text-center group">
                  <div className="text-2xl sm:text-3xl lg:text-5xl font-extrabold text-[#DAA520] mb-1 counter-animate">
                    <Counter target={s.val || '0'} suffix={s.suffix} />
                  </div>
                  <div className="text-[10px] sm:text-sm text-slate-400 uppercase tracking-wider font-medium">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =============== WHY CHOOSE US =============== */}
      <section className="py-14 sm:py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#DAA520]/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-8 h-[2px] bg-[#DAA520]" />
                <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Why Choose Us</span>
                <div className="w-8 h-[2px] bg-[#DAA520]" />
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0A0A0F]">Your Trusted Property Partner</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              { icon: Shield, title: 'Clear Titles', desc: 'Har project me 100% clear title guarantee with proper documentation', color: 'from-emerald-500 to-emerald-700' },
              { icon: TrendingUp, title: 'High Returns', desc: 'Strategic locations with 25-40% annual appreciation', color: 'from-amber-500 to-amber-700' },
              { icon: Award, title: 'Premium Quality', desc: 'World-class construction & amenities standards', color: 'from-blue-500 to-blue-700' },
              { icon: Handshake, title: '24/7 Support', desc: 'Dedicated team always available for you', color: 'from-purple-500 to-purple-700' },
            ].map((f, i) => (
              <Reveal key={i} delay={i + 1}>
                <div className="group bg-white rounded-2xl p-4 sm:p-7 shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift hover:border-[#DAA520]/20">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                    <f.icon size={22} className="text-white" />
                  </div>
                  <h3 className="font-bold text-sm sm:text-lg text-[#0A0A0F] mb-1 sm:mb-2">{f.title}</h3>
                  <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* =============== SERVICES =============== */}
      {settings?.services && settings.services.length > 0 && (
        <section className="py-14 sm:py-24 bg-[#FAFBFC]" data-testid="services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Reveal>
              <div className="text-center mb-10 sm:mb-16">
                <div className="inline-flex items-center gap-2 mb-3">
                  <div className="w-8 h-[2px] bg-[#DAA520]" />
                  <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Our Services</span>
                  <div className="w-8 h-[2px] bg-[#DAA520]" />
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0A0A0F]">What We Offer</h2>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {settings.services.map((s, i) => (
                <Reveal key={i} delay={Math.min(i + 1, 6)}>
                  <div className="group bg-white rounded-2xl p-5 sm:p-7 shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#DAA520] to-[#FFD700] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#DAA520] to-[#B8860B] flex items-center justify-center mb-4 text-[#0A0A0F] font-bold text-lg shadow-lg group-hover:scale-110 transition-transform">
                      {i + 1}
                    </div>
                    <h3 className="font-bold text-sm sm:text-lg text-[#0A0A0F] mb-1 sm:mb-2">{s.title}</h3>
                    {s.description && <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">{s.description}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* =============== FEATURED PROPERTIES =============== */}
      <section className="py-14 sm:py-24 bg-white" data-testid="featured-properties">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 sm:mb-14 gap-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-[2px] bg-[#DAA520]" />
                  <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Featured</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0A0A0F]">Handpicked for You</h2>
              </div>
              <Link to="/properties" className="inline-flex items-center gap-2 text-[#DAA520] font-bold text-sm hover:gap-3 transition-all group">
                View All <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
          {properties.length > 0 ? (
            <>
              <div className="flex gap-4 overflow-x-auto pb-4 sm:hidden scrollbar-hide snap-x snap-mandatory">
                {properties.map((p, i) => (
                  <div key={p.id} className="min-w-[280px] max-w-[300px] snap-start">
                    <Reveal delay={Math.min(i + 1, 3)}>
                      <PropertyCard property={p} />
                    </Reveal>
                  </div>
                ))}
              </div>
              <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((p, i) => (
                  <Reveal key={p.id} delay={Math.min(i + 1, 6)}>
                    <PropertyCard property={p} />
                  </Reveal>
                ))}
              </div>
            </>
          ) : (
            <Reveal>
              <div className="text-center py-16 bg-[#FAFBFC] rounded-3xl border border-slate-100">
                <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Properties coming soon. Contact us for details.</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* =============== PROPERTY TYPES =============== */}
      <section className="py-14 sm:py-24 bg-[#0A0A0F] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNDOUE4NEMiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2em0wIDM2YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <Reveal>
            <div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-8 h-[2px] bg-[#DAA520]" />
                <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Explore</span>
                <div className="w-8 h-[2px] bg-[#DAA520]" />
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">Find Your Perfect Property</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {[
              { icon: TreePine, label: 'Plots', slug: 'plots' },
              { icon: Building2, label: 'Flats', slug: 'flats' },
              { icon: Home, label: 'Villas', slug: 'villas' },
              { icon: Landmark, label: 'Commercial', slug: 'commercial-properties' },
              { icon: Home, label: 'Residential', slug: 'residential-properties' },
              { icon: TreePine, label: 'Farm House', slug: 'farm-houses' },
            ].map((t, i) => (
              <Reveal key={i} dir="scale" delay={Math.min(i + 1, 6)}>
                <Link to={`/location/${t.slug}-in-${(locations[0] || 'india').toLowerCase()}`}
                  className="group bg-[#1A1A25] rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center hover:bg-gradient-to-br hover:from-[#DAA520] hover:to-[#B8860B] transition-all duration-500 border border-[#1A1A25] hover:border-[#DAA520] active:scale-95 hover:shadow-[0_8px_40px_rgba(201,168,76,0.25)] block">
                  <t.icon size={24} className="mx-auto text-[#DAA520] group-hover:text-[#0A0A0F] transition-colors mb-2 sm:mb-3 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform" />
                  <span className="text-white group-hover:text-[#0A0A0F] font-bold text-xs sm:text-sm transition-colors">{t.label}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* =============== LOCATIONS =============== */}
      {locations.length > 0 && (
        <section className="py-14 sm:py-24 bg-[#FAFBFC]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Reveal>
              <div className="text-center mb-8 sm:mb-14">
                <div className="inline-flex items-center gap-2 mb-3">
                  <div className="w-8 h-[2px] bg-[#DAA520]" />
                  <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Locations</span>
                  <div className="w-8 h-[2px] bg-[#DAA520]" />
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0A0A0F]">Properties Across Prime Locations</h2>
              </div>
            </Reveal>
            <div className="flex gap-3 overflow-x-auto pb-4 sm:pb-0 sm:grid sm:grid-cols-3 lg:grid-cols-5 sm:gap-4 scrollbar-hide snap-x snap-mandatory">
              {locations.map((loc, i) => (
                <Reveal key={i} delay={Math.min(i + 1, 5)}>
                  <Link to={`/location/plots-in-${loc.toLowerCase().replace(/ /g, '-')}`}
                    className="shrink-0 min-w-[140px] sm:min-w-0 group bg-white rounded-2xl p-4 sm:p-6 text-center shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift hover:border-[#DAA520]/20 snap-start block">
                    <div className="w-12 h-12 rounded-xl bg-[#DAA520]/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#DAA520] group-hover:scale-110 transition-all duration-500">
                      <MapPin size={20} className="text-[#DAA520] group-hover:text-[#0A0A0F] transition-colors" />
                    </div>
                    <div className="font-bold text-sm text-[#0A0A0F] group-hover:text-[#DAA520] transition-colors">{loc}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1 group-hover:text-[#DAA520]">
                      View Properties <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* =============== TESTIMONIALS =============== */}
      {testimonials.length > 0 && (
        <section className="py-14 sm:py-24 bg-white" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Reveal>
              <div className="text-center mb-8 sm:mb-14">
                <div className="inline-flex items-center gap-2 mb-3">
                  <div className="w-8 h-[2px] bg-[#DAA520]" />
                  <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Testimonials</span>
                  <div className="w-8 h-[2px] bg-[#DAA520]" />
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0A0A0F]">What Our Clients Say</h2>
              </div>
            </Reveal>
            <div className="flex gap-4 overflow-x-auto pb-4 sm:hidden scrollbar-hide snap-x snap-mandatory">
              {testimonials.slice(0, 6).map((t, i) => (
                <div key={i} className="min-w-[300px] snap-start"><TestimonialCard t={t} /></div>
              ))}
            </div>
            <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((t, i) => (
                <Reveal key={i} delay={Math.min(i + 1, 6)}><TestimonialCard t={t} /></Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* =============== CTA =============== */}
      <section className="py-14 sm:py-24 bg-[#0A0A0F] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DAA520]/5 via-transparent to-[#DAA520]/5" />
        <div className="absolute top-10 right-20 w-40 h-40 bg-[#DAA520]/5 rounded-full blur-3xl animate-float hidden lg:block" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <Sparkles size={28} className="mx-auto text-[#DAA520] mb-4" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 sm:mb-6">Ready to Find Your Dream Property?</h2>
            <p className="text-slate-400 text-sm sm:text-base mb-8 max-w-2xl mx-auto">Get in touch with our experts and let us help you find the perfect property.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Link to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl hover:shadow-[0_8px_40px_rgba(201,168,76,0.4)] transition-all hover:scale-105 active:scale-95 text-sm sm:text-base animate-pulse-gold">
                Get Free Consultation <ArrowRight size={18} />
              </Link>
              <a href={`https://wa.me/${(settings?.whatsapp || settings?.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#25D366]/40 text-[#25D366] font-bold rounded-xl hover:bg-[#25D366]/10 transition-all text-sm sm:text-base hover:border-[#25D366]">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                WhatsApp Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </WebsiteLayout>
  );
}

/* ---- Property Card ---- */
const PropertyCard = ({ property }) => (
  <Link to={`/properties/${property.id}`} className="group block" data-testid={`property-card-${property.id}`}>
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift">
      <div className="h-44 sm:h-56 relative overflow-hidden">
        <img src={property.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=70'}
          alt={property.property_name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
        <div className="hidden absolute inset-0 bg-gradient-to-br from-[#1A1A25] to-[#0A0A0F] items-center justify-center">
          <Building2 size={48} className="text-[#DAA520]/30" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-[#DAA520] text-[#0A0A0F] text-[10px] sm:text-xs font-bold rounded-full shadow-lg">{property.property_type || 'Plot'}</span>
        </div>
        {(property.total_area || property.property_size) && (
          <div className="absolute bottom-3 right-3">
            <span className="px-2.5 py-1 bg-white/90 text-[#0A0A0F] text-[10px] sm:text-xs font-bold rounded-lg backdrop-blur shadow-sm">{property.total_area || property.property_size}</span>
          </div>
        )}
      </div>
      <div className="p-4 sm:p-5">
        <h3 className="font-bold text-base sm:text-lg text-[#0A0A0F] group-hover:text-[#DAA520] transition-colors mb-1.5 line-clamp-1">{property.property_name}</h3>
        <div className="flex items-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-2">
          <MapPin size={13} className="text-[#DAA520]" /> <span>{property.location || 'Prime Location'}</span>
        </div>
        {property.price_range && <div className="text-[#DAA520] font-bold text-base sm:text-lg">{property.price_range}</div>}
        <div className="mt-3 flex items-center text-xs sm:text-sm font-semibold text-[#DAA520] group-hover:gap-3 gap-2 transition-all">
          View Details <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </div>
  </Link>
);

/* ---- Testimonial Card ---- */
const TestimonialCard = ({ t }) => (
  <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift h-full">
    <div className="flex gap-0.5 mb-3">
      {[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={14} className="fill-[#DAA520] text-[#DAA520]" />)}
    </div>
    <p className="text-slate-600 text-sm leading-relaxed mb-4 italic line-clamp-3">"{t.review}"</p>
    <div className="flex items-center gap-3 border-t border-slate-100 pt-3">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#DAA520] to-[#B8860B] flex items-center justify-center text-white font-bold text-sm shadow-lg">
        {(t.customer_name || 'C')[0]}
      </div>
      <div>
        <div className="font-bold text-sm text-[#0A0A0F]">{t.customer_name}</div>
        <div className="text-xs text-slate-500">{t.location || t.property_type}</div>
      </div>
    </div>
  </div>
);
